import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as limitQuery,
  startAfter,
  endBefore,
  limitToLast,
  onSnapshot,
  writeBatch,
  runTransaction,
  type QueryConstraint,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Query,
  type CollectionReference,
  type Firestore,
  type Unsubscribe,
  type DocumentSnapshot,
  type WhereFilterOp,
  type OrderByDirection,
} from 'firebase/firestore';
import { db } from './firebase-setup';
import type { WithId } from './types';
import type { 
  QueryOptions, 
  WhereClause, 
  OrderByClause 
} from './types/query-options';

// Re-export commonly used types for backward compatibility
export type { WithId } from './types';
export type { QueryOptions, WhereClause, OrderByClause } from './types/query-options';

export class FirestoreService<T extends DocumentData> {
  private readonly collectionRef: CollectionReference<T>;

  constructor(collectionName: string, private readonly firestore: Firestore = db) {
    this.collectionRef = collection(this.firestore, collectionName) as CollectionReference<T>;
  }

  // Create
  async create(id: string, data: T): Promise<WithId<T>> {
    const docRef = doc(this.collectionRef, id);
    await setDoc(docRef, data);
    return { id, ...data };
  }

  // Read
  async get(id: string): Promise<WithId<T> | null> {
    const docRef = doc(this.collectionRef, id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  }

  // Update
  async update(id: string, data: Partial<T>): Promise<void> {
    const docRef = doc(this.collectionRef, id);
    await updateDoc(docRef, data as any);
  }

  // Delete
  async delete(id: string): Promise<void> {
    const docRef = doc(this.collectionRef, id);
    await deleteDoc(docRef);
  }

  // Query with filters
  async query(options: QueryOptions<T> = {}): Promise<WithId<T>[]> {
    let q: Query<T> = this.collectionRef;
    const queryConstraints: QueryConstraint[] = [];

    // Apply where clauses
    if (options.where && options.where.length > 0) {
      for (const clause of options.where) {
        queryConstraints.push(
          where(
            clause.field as string,
            clause.operator,
            clause.value
          )
        );
      }
    }

    // Apply order by
    if (options.orderBy && options.orderBy.length > 0) {
      for (const clause of options.orderBy) {
        queryConstraints.push(
          orderBy(
            clause.field as string,
            clause.direction || 'asc'
          )
        );
      }
    }

    // Apply pagination
    if (options.limit) {
      if (options.last) {
        queryConstraints.push(limitToLast(options.limit));
      } else {
        queryConstraints.push(limitQuery(options.limit));
      }
    }

    if (options.startAfter) {
      queryConstraints.push(startAfter(options.startAfter));
    }

    if (options.endBefore) {
      queryConstraints.push(endBefore(options.endBefore));
    }

    // Apply all constraints
    if (queryConstraints.length > 0) {
      q = query(this.collectionRef, ...queryConstraints);
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    } as WithId<T>));
  }

  // Subscribe to real-time updates
  subscribe(
    callback: (data: WithId<T>[]) => void,
    options: Omit<QueryOptions<T>, 'startAfter' | 'endBefore' | 'last'> = {}
  ): Unsubscribe {
    let q: Query<T> = this.collectionRef;
    const queryConstraints: QueryConstraint[] = [];

    // Apply where clauses
    if (options.where && options.where.length > 0) {
      for (const clause of options.where) {
        queryConstraints.push(
          where(
            clause.field as string,
            clause.operator,
            clause.value
          )
        );
      }
    }


    // Apply order by
    if (options.orderBy && options.orderBy.length > 0) {
      for (const clause of options.orderBy) {
        queryConstraints.push(
          orderBy(
            clause.field as string,
            clause.direction || 'asc'
          )
        );
      }
    }
    // Apply limit
    if (options.limit) {
      queryConstraints.push(limitQuery(options.limit));
    }

    // Apply all constraints
    if (queryConstraints.length > 0) {
      q = query(this.collectionRef, ...queryConstraints);
    }

    return onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      } as WithId<T>));
      callback(data);
    });
  }

  // Batch operations
  static createBatch() {
    return writeBatch(db);
  }

  // Transaction support
  static async runTransaction<T>(
    updateFunction: (transaction: any) => Promise<T>
  ): Promise<T> {
    return runTransaction(db, updateFunction);
  }
}
